import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {buildCardGroupSearchUrl, SearchCardGroupOptions} from '../../../helpers/models/cardGroupHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {ApiCardGroup, ApiCardGroups, CardGroupModel} from '../../../models/api/cardGroupModel';
import {ApiCards, CardModel} from '../../../models/api/cardModel';
import {ContactModel} from '../../../models/api/contactModel';
import {ApiSearchCardGroups, SearchCardGroupsModel} from '../../../models/api/searchCardGroupModel';
import {ApiSearchNamespacedCards} from '../../../models/api/searchCardModel';
import {
  ApiSendOptions,
  deleteAsync,
  fetchApiAsync,
  postApiAsync,
  postAsync,
  putApiAsync,
} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {findCardLock} from '../../../services/locks/resourceLocks';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {DataState} from '../dataState';

/*
 * Props.
 */

export interface CreateCardGroupParams {
  name: string;
  namespace: string;
}

export interface FetchCardsForGroupOptions extends ApiSendOptions {
  cardGroupId: number;
  limit?: number;
  offset?: number;
}

export interface FetchCardGroupsOptions extends ApiSendOptions {
  limit?: number;
  pageToken?: string;
}

export interface FetchCardGroupsResponse {
  cardGroups: ReadonlyArray<CardGroupModel>;
  next?: string;
}

export interface FetchCardsForGroupResponse {
  cards: ReadonlyArray<CardModel>;
  cardGroups: ReadonlyArray<CardGroupModel>;
  contacts: ReadonlyArray<ContactModel>;
  limit: number;
}

export interface EditCardGroupOptions {
  cardGroupId: number;
  name: string;
}

export interface AddCardGroupToCardsOptions extends ApiSendOptions {
  cardGroupId: number;
  cardIds: ReadonlyArray<number>;
}

/*
 * Actions types.
 */

export enum CardGroupActionsEnum {
  CARD_GROUP_DELETED = '@@data/cardGroups/CARD_GROUP_DELETED',
}

interface CardGroupDeletedActionPayload {
  cardGroupId: number;
}

export interface CardGroupDeletedAction extends ActionWithPayload<CardGroupDeletedActionPayload> {
  type: CardGroupActionsEnum.CARD_GROUP_DELETED;
}

/*
 * Action creators.
 */

export function cardGroupDeleted(cardGroupId: number) {
  return {
    type: CardGroupActionsEnum.CARD_GROUP_DELETED,
    payload: {
      cardGroupId,
    },
  };
}

/*
 * Union type.
 */

export type CardGroupAction = CardGroupDeletedAction;

/*
 * Actions.
 */

export function createCardGroupAsyncAction(params: CreateCardGroupParams): ThunkAsyncAction<CardGroupModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;
    const createUrl = `${company.url}/card_groups`;

    const cardGroup = await postApiAsync(ApiCardGroup, createUrl, params);
    dispatch(
      updateDataAction({
        cardGroups: [cardGroup],
      }),
    );
    return cardGroup;
  };
}

export function removeCardGroupFromCardAsyncAction(
  group: CardGroupModel,
  cardId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'cards'>(
      'cards',
      createResourceMapValueUpdater(
        cardId,
        createUpdater<CardModel>((card) => ({
          ...card,
          groupIds: card.groupIds.filter((groupId) => groupId !== group.id),
        })),
      ),
    );
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCardLock(cardId).lockAsync(async () => {
          const {company} = getState().data;
          const ungroupUrl = buildBatchUngroupUrl(company.url, group.id);

          try {
            const cards = await postApiAsync(ApiCards, ungroupUrl, {card_ids: [cardId]}, options);
            return updateDataAction({
              cards,
            });
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}

export function addCardGroupToCardAsyncAction(
  group: CardGroupModel,
  cardId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'cards'>(
      'cards',
      createResourceMapValueUpdater(
        cardId,
        createUpdater<CardModel>((card) => ({
          ...card,
          groupIds: Array.from(new Set([...card.groupIds, group.id])),
        })),
      ),
    );
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCardLock(cardId).lockAsync(async () => {
          const {company} = getState().data;
          const groupUrl = buildBatchGroupUrl(company.url, group.id);

          try {
            const cards = await postApiAsync(ApiCards, groupUrl, {card_ids: [cardId]}, options);
            return updateDataAction({
              cards,
            });
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }

            return undefined;
          }
        }),
      ),
    );
  };
}

export function addCardGroupToCardsAsyncAction(options: AddCardGroupToCardsOptions): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const {cardGroupId, cardIds} = options;
    const companyUrl = getCompanyUrl(getState());
    const groupUrl = buildBatchGroupUrl(companyUrl, cardGroupId);

    try {
      const cards = await postApiAsync(ApiCards, groupUrl, {card_ids: cardIds}, options);
      dispatch(
        updateDataAction({
          cards,
        }),
      );
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function addCardsToCardGroupsAsyncAction(
  groupIds: ReadonlyArray<number>,
  cardIds: ReadonlyArray<number>,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    const addGroupsCardPromises = groupIds.map((groupId) =>
      postApiAsync(ApiCards, buildBatchGroupUrl(companyUrl, groupId), {card_ids: cardIds}, options),
    );
    const responseCards = await Promise.all(addGroupsCardPromises);
    batch(() => {
      responseCards.forEach((cards) =>
        dispatch(
          updateDataAction({
            cards,
          }),
        ),
      );
    });
  };
}

export function removeCardGroupFromCardsAsyncAction(
  groupId: number,
  cardIds: ReadonlyArray<number>,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const ungroupUrl = buildBatchUngroupUrl(companyUrl, groupId);

    try {
      await postAsync(ungroupUrl, {card_ids: cardIds}, options);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function removeCardsFromCardGroupsAsyncAction(
  groupIds: ReadonlyArray<number>,
  cardIds: ReadonlyArray<number>,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    const removeGroupsCardPromises = groupIds.map((groupId) =>
      postApiAsync(ApiCards, buildBatchUngroupUrl(companyUrl, groupId), {card_ids: cardIds}, options),
    );
    const responseCards = await Promise.all(removeGroupsCardPromises);
    batch(() => {
      responseCards.forEach((cards) =>
        dispatch(
          updateDataAction({
            cards,
          }),
        ),
      );
    });
  };
}

export function updateCardGroupAction(newCardGroup: CardGroupModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing card group.
    const existingCardGroup = getState().data.cardGroups[newCardGroup.id];

    // No need to do anything if the new card is older.
    if (existingCardGroup && existingCardGroup.updatedAt > newCardGroup.updatedAt) {
      return null;
    }

    // Otherwise, update the card.
    return dispatch(updateDataAction({cardGroups: [newCardGroup], updatedAt: newCardGroup.updatedAt}));
  };
}

export function editCardGroupAsyncAction({
  cardGroupId,
  name,
}: EditCardGroupOptions): ThunkAsyncAction<CardGroupModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    const updateCardGroupUrl = `${companyUrl}/card_groups/${cardGroupId}`;

    try {
      const cardGroup = await putApiAsync(ApiCardGroup, updateCardGroupUrl, {name});
      dispatch(updateCardGroupAction(cardGroup));
      return cardGroup;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteCardGroupsAsyncAction(cardGroupIds: ReadonlyArray<number>): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    const deleteCardGroupPromises = cardGroupIds.map((cardGroupId) =>
      deleteAsync(`${companyUrl}/card_groups/${cardGroupId}`),
    );
    try {
      await Promise.all(deleteCardGroupPromises);
      batch(() => {
        cardGroupIds.forEach((cardGroupId) => dispatch(cardGroupDeleted(cardGroupId)));
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchCardGroupAction(
  cardGroupId: number,
  options?: ApiSendOptions,
): ThunkAsyncAction<CardGroupModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const fetchCardGroupUrl = `${company.url}/card_groups/${cardGroupId}`;

    try {
      const cardGroup = await fetchApiAsync(ApiCardGroup, fetchCardGroupUrl, options);
      dispatch(updateDataAction({cardGroups: [cardGroup]}));
      return cardGroup;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchCardGroupsAndFilterPrivateNamespaceAsyncAction(
  namespace: string,
  cancelToken?: CancelToken,
): ThunkAsyncAction<ReadonlyArray<CardGroupModel>> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const fetchGroupsUrl = `${companyUrl}/card_groups`;

    try {
      const cardGroups = await fetchApiAsync(ApiCardGroups, fetchGroupsUrl, {cancelToken});
      // For private contacts, only show private groups.
      return isTeammateNamespace(namespace)
        ? cardGroups.filter((group) => group.namespace === namespace)
        : cardGroups;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      throw error;
    }
  };
}

export function fetchCardsForCardGroupAsyncAction(
  options: FetchCardsForGroupOptions,
): ThunkAsyncAction<FetchCardsForGroupResponse> {
  return async (_dispatch, getState) => {
    const {cardGroupId} = options;
    const companyUrl = getCompanyUrl(getState());
    const fetchCardsForGroupUrl = addQueryToPath(`${companyUrl}/card_groups/${cardGroupId}/cards`, {
      limit: options?.limit,
      offset: options?.offset,
    });

    try {
      const {cards, contacts, cardGroups, limit} = await fetchApiAsync(
        ApiSearchNamespacedCards,
        fetchCardsForGroupUrl,
      );
      return {
        cards,
        contacts,
        cardGroups,
        limit,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      throw error;
    }
  };
}

export function fetchCardGroupsAsyncAction(
  options?: FetchCardGroupsOptions,
): ThunkAsyncAction<FetchCardGroupsResponse> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());

    const fetchGroupsUrl = addQueryToPath(`${companyUrl}/card_groups`, {
      limit: options?.limit,
      page_token: options?.pageToken,
    });

    try {
      const {cardGroups, next} = await fetchApiAsync(ApiSearchCardGroups, fetchGroupsUrl, options);
      return {cardGroups, next};
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      throw error;
    }
  };
}

export function searchCardGroupsAsyncAction(
  query: string,
  options: ApiSendOptions & SearchCardGroupOptions,
): ThunkAsyncAction<SearchCardGroupsModel> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const searchUrl = buildCardGroupSearchUrl(companyUrl, query, options);

    try {
      const response = await fetchApiAsync(ApiSearchCardGroups, searchUrl, options);
      return response;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      throw error;
    }
  };
}

/*
 * Helpers.
 */

function buildBatchGroupUrl(companyUrl: string, groupId: number) {
  return `${companyUrl}/card_batch/group/${groupId}`;
}

function buildBatchUngroupUrl(companyUrl: string, groupId: number) {
  return `${companyUrl}/card_batch/ungroup/${groupId}`;
}
